import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { useAuth0 } from '@auth0/auth0-vue'
import { useI18n } from 'vue-i18n'
import { loadLanguageAsync } from '../i18n'

interface Skill {
  code: string
  name: string
}

const GET_USER_PROFILE = gql`
  query GetUserLanguage($request: UserReq!) {
    getUserLanguage(request: $request) {
      languageCode
    }
  }
`

const UPDATE_USER_LANGUAGE = gql`
  mutation UpdateUserLanguage($request: UpdateLanguageReq!) {
    updateUserLanguage(request: $request) {
      languageCode
    }
  }
`

const ADD_USER_REGISTRATION = gql`
  mutation RegisterUser($request: AddUserRequest!) {
    registerUser(request: $request) {
      id
      userId
      userName
      createdAt
      loginHistory
      hero {
        id
        name
        description
        imageUrl
      }
      newsletter
      agreeTerms
      userEmail
    }
  }
`

const SET_SKILLS_TO_USER = gql`
  mutation SetSkillsToUser($request: SetSkillsToUserRequest!) {
    setSkillsToUser(request: $request) {
      skillOnTraining {
        name
        timesParticipated
        timesScored
        relatedWarriors
      }
    }
  }
`

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }
  return null
}

function getBrowserLanguage(): string {
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language || 'en'
    return browserLang.split('-')[0]
  }
  return 'en'
}

export const useUserStore = defineStore(
  'user',
  () => {
    const { user: auth0User } = useAuth0()
    const { locale } = useI18n({ useScope: 'global' })

    const supportedLanguages = ['gb', 'es', 'cn', 'en', 'ca', 'de', 'fr', 'nl', 'el', 'et', 'it', 'lt', 'lv', 'pl']
    const cookieLang = getCookie('user_language')
    const browserLang = getBrowserLanguage()

    // REMOVED: isRegistrationComplete ref

    const { mutate: registerUser } = useMutation(ADD_USER_REGISTRATION)
    const { mutate: setSkillsToUser } = useMutation(SET_SKILLS_TO_USER)
    const { onResult, onError } = useQuery(
      GET_USER_PROFILE,
      () => ({
        request: {
          userId: auth0User.value?.sub,
          userName: auth0User.value?.name,
        },
      }),
      () => ({
        enabled: !!auth0User.value?.sub,
        fetchPolicy: 'network-only',
      }),
    )
    const { mutate: updateUserLanguageMutation } = useMutation(UPDATE_USER_LANGUAGE, {
      refetchQueries: ['GetUserLanguage'],
    })

    let startLang = 'en'

    if (cookieLang && supportedLanguages.includes(cookieLang)) {
      startLang = cookieLang
    } else if (supportedLanguages.includes(browserLang)) {
      startLang = browserLang
    }

    // --- STATE ---
    const profile = ref({
      languageCode: startLang,
      isLoading: !!auth0User.value,
    })

    const registrationData = ref({
      userEmail: '',
      isOver18: true,
      wantsNewsletter: false,
      selectedHeroName: null as string | null,
      selectedSkills: [] as Skill[],
    })

    locale.value = startLang

    // --- WATCHER ---
    watch(
      () => profile.value.languageCode,
      async (newLanguageCode) => {
        if (newLanguageCode && supportedLanguages.includes(newLanguageCode)) {
          await loadLanguageAsync(newLanguageCode)
          locale.value = newLanguageCode
          document.cookie = `user_language=${newLanguageCode}; path=/; max-age=31536000`
        }
      },
      { immediate: true },
    )

    onResult((result) => {
      const langFromServer = result.data?.getUserLanguage?.languageCode
      if (langFromServer && supportedLanguages.includes(langFromServer)) {
        profile.value.languageCode = langFromServer
      }
      profile.value.isLoading = false
    })

    onError((_error) => {
      profile.value.isLoading = false
    })

    function updateLanguagePreferenceSession(newLanguageCode: string) {
      if (newLanguageCode && supportedLanguages.includes(newLanguageCode)) {
        profile.value.languageCode = newLanguageCode
      }
    }

    async function updateLanguagePreference(newLanguageCode: string) {
      if (newLanguageCode !== '') {
        profile.value.languageCode = newLanguageCode
      }

      if (auth0User.value?.sub) {
        try {
          await updateUserLanguageMutation({
            request: {
              userId: auth0User.value?.sub,
              userName: auth0User.value?.name,
              languageCode: profile.value.languageCode,
            },
          })
        } catch (error) {
          console.error('Failed to save language to DB:', error)
        }
      }
    }

    // REWRITTEN: Async registration action returning a promise/boolean
    async function performRegistration(payload?: any) {
      console.log('user data - email ' + auth0User.value?.email)

      try {
        await registerUser({
          request: {
            userId: auth0User.value?.sub,
            userName: auth0User.value?.name,
            isOver18: true, // You may want to swap these to registrationData.value in the future
            wantsNewsletter: true,
            selectedHeroName: 'the_chief_strategist',
            userEmail: auth0User.value?.email,
            ...payload, // Merge any passed payload explicitly
          },
        })

        const skillCodes = registrationData.value.selectedSkills.map((skill) => skill.code)
        if (skillCodes.length === 0) {
          skillCodes.push('superiorRiskAssessment')
        }

        await setSkillsToUser({
          request: {
            userId: auth0User.value?.sub,
            userName: auth0User.value?.name,
            skillsList: {
              skills: skillCodes,
            },
          },
        })

        // Ensure this is awaited so the flow doesn't finish prematurely
        await updateLanguagePreference('')

        // Return true to the calling component so it knows the sequence finished successfully
        return true
      } catch (error) {
        console.error('Failed during user registration sequence:', error)
        // Throwing the error allows the calling component's try/catch block to handle failures
        throw error
      }
    }

    function updateRegistrationData(data: Partial<typeof registrationData.value>) {
      registrationData.value = { ...registrationData.value, ...data }
    }

    function clearRegistrationData() {
      registrationData.value = {
        userEmail: '',
        isOver18: true,
        wantsNewsletter: false,
        selectedHeroName: null,
        selectedSkills: [],
      }
    }

    return {
      profile,
      registrationData,
      updateLanguagePreference,
      updateLanguagePreferenceSession,
      updateRegistrationData,
      clearRegistrationData,
      performRegistration, // Exported the new function name
    }
  },
  {
    persist: true,
  },
)
